// @ts-check
// Settings → Appearance → Time Zone: the one time zone a coach chooses.
//
// ⚠️ ONE CHOICE, KEPT IN TWO PLACES. The CRM's dates (check-in days, payments,
// tasks) read settings.timeZone from the settings blob (config.js
// getCoachTimezone), and every booking rule on the server reads
// coaches.settings.scheduling.timezone, which is also the calendar's home
// time. They used to be two separate choices, this card and one on the Online
// Booking card, so a coach could set them differently. This card is the only
// place to choose now, and Save writes both: the server first, because it can
// refuse (clients' weekly standing sessions are pinned to the booking zone),
// then the blob, so the two never disagree after a save.

import { callFunction } from './api.js';
import { loadSettings, saveSettingsToStorage } from './config.js';
import { timeZoneOptionsHtml } from './time-zone-options.js';
import { primeHomeZone } from './coach-calendar/format.js';
import { showToast, syncUpgradedSelects } from './utils.js';

/** @returns {string} */
function computerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

/**
 * What the card shows, and what is not saved yet. Pure.
 *
 * The zone booking runs in wins when the coach saved one: it is the one that
 * decides real session times. Otherwise the CRM's own zone, else this
 * computer's. The hint names whichever of the two still runs on something
 * else, so the coach knows what Save will change.
 *
 * @param {{ crmZone: string, bookingZone: string | null, bookingSaved: boolean, computer: string }} s
 *   bookingZone is null when the server could not be asked.
 * @returns {{ shown: string, hint: string }}
 */
export function timeZoneState({ crmZone, bookingZone, bookingSaved, computer }) {
  const crmEffective = crmZone || computer;
  const shown = (bookingZone && bookingSaved) ? bookingZone : (crmEffective || bookingZone || '');
  let hint = '';
  if (bookingZone && shown !== bookingZone) hint = `Online booking runs on ${bookingZone} until you press Save.`;
  else if (crmEffective && shown !== crmEffective) hint = `Your CRM dates use ${crmEffective} until you press Save.`;
  return { shown, hint };
}

/**
 * The booking zone as the server last told us, or null when it could not be read.
 * @type {{ zone: string, saved: boolean } | null}
 */
let _booking = null;

function paint() {
  const select = /** @type {HTMLSelectElement | null} */ (document.getElementById('s-timezone'));
  if (!select) return;
  const hint = document.getElementById('s-tz-hint');
  const save = /** @type {HTMLButtonElement | null} */ (document.getElementById('btn-save-timezone'));
  const { shown, hint: text } = timeZoneState({
    crmZone: loadSettings()?.timeZone || '',
    bookingZone: _booking?.zone || null,
    bookingSaved: !!_booking?.saved,
    computer: computerZone(),
  });
  select.innerHTML = timeZoneOptionsHtml(shown);
  select.value = shown;
  // The select is drawn as a custom dropdown; the second paint lands after
  // that is built, so it must be told, or it shows the first paint's zone.
  syncUpgradedSelects(select.parentElement);
  if (hint) hint.textContent = text;
  // Something is not saved yet, so Save has work to do. The dirty tracker in
  // app.js only lights it when the select changes, and nothing changed here.
  if (text && save) save.disabled = false;
}

/** Paints the card from this browser at once, then again once the server says where booking runs. */
export async function renderTimeZoneSetting() {
  paint();
  // A team member never sees this tab (it is the owner's), and has no
  // coaches row for the server to read the booking zone from.
  if (/** @type {any} */ (window)._appMember) return;
  try {
    const res = await callFunction('coach-availability-get', {});
    const zone = res?.scheduling?.timezone;
    _booking = zone ? { zone, saved: res?.timezoneSaved !== false } : null;
  } catch (e) {
    console.warn('[time zone] could not read the booking zone', e);
    _booking = null;
  }
  paint();
}

/**
 * Saves the selected zone for booking (server) and then for the CRM's dates
 * (blob). A refusal leaves both as they were.
 * @returns {Promise<boolean>} true when saved
 */
export async function saveTimeZone() {
  const select = /** @type {HTMLSelectElement | null} */ (document.getElementById('s-timezone'));
  const hint = document.getElementById('s-tz-hint');
  const zone = select?.value || '';
  if (!zone) return false;
  try {
    await callFunction('coach-availability-update', { timezone: zone });
  } catch (e) {
    const message = (e && /** @type {Error} */ (e).message) || 'Could not save the time zone. Try again.';
    if (hint) hint.textContent = message;
    showToast(message, 'error');
    return false;
  }
  const settings = loadSettings();
  settings.timeZone = zone;
  saveSettingsToStorage(settings);
  _booking = { zone, saved: true };
  // The calendar reads its home time once per page; give it the new one now,
  // or a standing session set from it before a reload would use the old zone.
  primeHomeZone({ scheduling: { timezone: zone } });
  if (hint) hint.textContent = '';
  showToast('Time zone saved.', 'success');
  return true;
}
